import SendFeedbackDialog from "../components/SendFeedbackDialog";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { formsAPI } from "../lib/api";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import SignaturePad from "../components/SignaturePad";
import { isEmbeddedFormImage } from "../lib/formContent";
import FormContentText from "../components/FormContentText";
import LinkifiedText from "../components/LinkifiedText";
import { AlertCircle, Check, FileText, Loader2, Send } from "lucide-react";
import "./PublicForm.css";
import { AppSelect } from '../components/ui/app-select';

const resolveApiErrorMessage = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

function normalizeFieldWidth(field) {
  const value = String(field?.width || "").trim().toLowerCase();
  if (value === "half" || value === "third") return value;
  return "full";
}

function getFieldWidthUnits(field) {
  const width = normalizeFieldWidth(field);
  if (width === "third") return 2;
  if (width === "half") return 3;
  return 6;
}

function getFieldSpanClass(field) {
  const units = getFieldWidthUnits(field);
  if (units === 2) return "md:col-span-2";
  if (units === 3) return "md:col-span-3";
  return "md:col-span-6";
}

function buildFieldRows(fields) {
  const rows = [];
  let currentRow = [];
  let currentUnits = 0;

  (Array.isArray(fields) ? fields : []).forEach((field) => {
    const units = getFieldWidthUnits(field);
    if (currentUnits + units > 6) {
      rows.push(currentRow);
      currentRow = [];
      currentUnits = 0;
    }
    currentRow.push(field);
    currentUnits += units;
    if (currentUnits === 6) {
      rows.push(currentRow);
      currentRow = [];
      currentUnits = 0;
    }
  });

  if (currentRow.length) rows.push(currentRow);
  return rows;
}

export default function PublicForm() {
  const { token, slugCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(null);
  const [values, setValues] = useState({});
  const [contact, setContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
  });
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const submittingRef = useRef(false);
  const successRef = useRef(null);
  const fieldRefs = useRef({});
  const contactFullName = `${contact.first_name || ""} ${contact.last_name || ""}`.trim()
    || `${form?.client?.first_name || ""} ${form?.client?.last_name || ""}`.trim();
  const displayedError = Object.keys(validationErrors).length > 0
    ? "Bitte alle rot markierten Pflichtfelder ausfüllen."
    : error;
  const headerColor = String(form?.header_color || "").trim() || "#1D1D1F";
  const resolvePrivacySource = (field) => {
    const source = String(field?.privacy_policy_source || "studio").toLowerCase();
    return source === "custom" ? "custom" : "studio";
  };

  useEffect(() => {
    let active = true;
    (async () => {
      const publicId = slugCode || token;
      if (!publicId) { setLoading(false); setError("Dieser Formularlink ist ungültig."); return; }
      setLoading(true);
      setDone(false);
      setForm(null);
      setError("");
      setValidationErrors({});
      fieldRefs.current = {};
      try {
        const res = slugCode ? await formsAPI.publicGetShort(slugCode) : await formsAPI.publicGet(token);
        if (!active) return;
        const data = res.data || null;
        setForm(data);
        const initial = {};
        (data?.fields || []).forEach((field) => {
          if (field.type === "content") return;
          initial[field.key] = field.type === "checkbox" || field.type === "privacy" ? false : "";
        });
        setValues(initial);
        setContact({
          first_name: String(data?.client?.first_name || ""),
          last_name: String(data?.client?.last_name || ""),
          email: String(data?.client?.email || ""),
          phone: String(data?.client?.phone || ""),
        });
      } catch (e) {
        if (!active) return;
        setError(resolveApiErrorMessage(e, "Formular konnte nicht geladen werden."));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [slugCode, token]);

  useEffect(() => {
    if (done) {
      successRef.current?.focus({ preventScroll: true });
      successRef.current?.scrollIntoView({ behavior: "auto", block: "center" });
    }
  }, [done]);

  const clearValidationError = (key) => {
    setValidationErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const validateBeforeSubmit = () => {
    const invalid = {};
    if (form.show_contact_details && form.require_contact_details) {
      if (!String(contact.first_name || "").trim()) invalid["contact:first_name"] = "Pflichtfeld";
      if (!String(contact.last_name || "").trim()) invalid["contact:last_name"] = "Pflichtfeld";
      if (!String(contact.email || "").trim()) invalid["contact:email"] = "Pflichtfeld";
    }
    const fields = Array.isArray(form?.fields) ? form.fields : [];
    for (const field of fields) {
      if (field.type === "content") continue;
      if (!field.required) continue;
      const value = values[field.key];
      if (field.type === "checkbox" || field.type === "privacy") {
        if (!Boolean(value)) {
          invalid[`field:${field.key}`] = field.type === "privacy" ? "Bitte zustimmen." : "Pflichtfeld";
        }
      } else if (field.type === "signature") {
        if (!value || (typeof value === "string" && !value.startsWith("data:image/"))) {
          invalid[`field:${field.key}`] = "Bitte unterschreiben.";
        }
      } else if (!String(value || "").trim()) {
        invalid[`field:${field.key}`] = "Pflichtfeld";
      }
    }
    return invalid;
  };

  const scrollToFirstInvalidField = (invalid) => {
    const firstKey = Object.keys(invalid)[0];
    if (!firstKey) return;
    window.requestAnimationFrame(() => {
      const target = fieldRefs.current[firstKey];
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusTarget = target.querySelector("input:not([type=hidden]), textarea, select, button, [tabindex]");
      focusTarget?.focus({ preventScroll: true });
    });
  };

  const submit = async () => {
    const publicId = slugCode || token;
    if (!publicId || !form || submittingRef.current || done) return;
    const invalid = validateBeforeSubmit();
    if (Object.keys(invalid).length > 0) {
      setValidationErrors(invalid);
      setError("");
      scrollToFirstInvalidField(invalid);
      return;
    }
    setValidationErrors({});
    setError("");
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await (slugCode
        ? formsAPI.publicSubmitShort(slugCode, {
            data: values,
            contact_details: form.show_contact_details ? contact : {},
          })
        : formsAPI.publicSubmit(token, {
        data: values,
        contact_details: form.show_contact_details ? contact : {},
      }));
      setDone(true);
    } catch (e) {
      setError(resolveApiErrorMessage(e, "Absenden fehlgeschlagen."));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) {
    return <main className="public-form-page flex min-h-screen items-center justify-center" aria-busy="true">
      <div role="status" className="flex items-center gap-3 text-sm text-[#6B7280]"><Loader2 className="h-5 w-5 motion-safe:animate-spin" /> Formular wird geladen …</div>
    </main>;
  }

  if (!form) {
    return <main className="public-form-page flex min-h-screen items-center justify-center p-6">
      <div role="alert" className="max-w-md rounded-3xl border border-[#E5E5EA] bg-white p-8 text-center shadow-sm">
        <AlertCircle className="mx-auto mb-4 h-8 w-8 text-[#86868B]" />
        <h1 className="text-lg font-semibold">Formular nicht verfügbar</h1>
        <p className="mt-3 text-sm leading-6 text-[#6B7280]">{error || "Bitte prüfe den Link oder fordere einen neuen Formularlink an."}</p>
      </div>
    </main>;
  }

  return (
    <main className="public-form-page min-h-screen bg-[#F5F5F7] px-4 py-6 sm:px-6 sm:py-12">
      <SendFeedbackDialog state={submitting?"sending":"idle"} channel="Formular" detail="Dein Formular wird sicher übermittelt."/>
      <Dialog open={privacyModalOpen} onOpenChange={setPrivacyModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Datenschutz</DialogTitle>
          </DialogHeader>
          <LinkifiedText className="text-sm text-[#1D1D1F]">
            {String(form?.legal_privacy_text || "").trim()}
          </LinkifiedText>
        </DialogContent>
      </Dialog>
      {/* Aufklärungsbögen tragen lange Textblöcke. 4xl gibt den Feldern Platz und
          hält die Zeilen kurz genug, um sie noch angenehm zu lesen. */}
      <div className="max-w-4xl mx-auto">
        <Card className="overflow-hidden rounded-3xl border border-[#E5E5EA]/70 bg-white shadow-sm">
          <CardHeader className="space-y-0 overflow-hidden border-b border-[#E5E5EA] p-0">
            <div className="h-1.5" style={{ backgroundColor: headerColor }} />
            <div className="px-6 py-6 sm:px-8">
              <div className="mb-4 flex items-center gap-2 text-xs font-medium text-[#86868B]"><FileText className="h-4 w-4" /> Digitales Formular</div>
              <h1 className="public-form-title text-lg font-semibold leading-relaxed text-[#1D1D1F]">{String(form.name || "Formular").normalize("NFC")}</h1>
              {contactFullName ? <p className="mt-2 text-sm text-[#6B7280]">Für {contactFullName}</p> : null}
              {form.description && <p className="mt-3 whitespace-pre-line text-sm leading-6 text-[#6B7280]">{form.description}</p>}
            </div>
          </CardHeader>
          <CardContent className="space-y-6 p-6 sm:p-8" aria-busy={submitting}>
            {displayedError && <p className="text-sm text-[#FF3B30]" role="alert">{displayedError}</p>}
            {done ? (
              <section ref={successRef} tabIndex={-1} role="status" className="public-form-success px-2 py-10 text-center outline-none sm:py-14">
                <div className="public-form-success-icon mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-700"><Check className="h-8 w-8" strokeWidth={2} aria-hidden="true" /></div>
                <h2 className="text-xl font-semibold tracking-tight text-[#1D1D1F]">Vielen Dank!</h2>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-[#6B7280]">Dein Formular wurde erfolgreich übermittelt. Du kannst diese Seite jetzt schließen.</p>
                <span className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#F5F5F7] px-3 py-1.5 text-xs text-[#6B7280]"><Check className="h-3.5 w-3.5" /> Übermittlung abgeschlossen</span>
              </section>
            ) : (
              <fieldset disabled={submitting} className="min-w-0 space-y-6">
                <p className="text-sm leading-6 text-[#6B7280]">Bitte lies das Formular in Ruhe durch und ergänze deine Angaben. Mit * markierte Felder sind Pflichtfelder.</p>
                {form.show_contact_details && (
                  <div className="space-y-4 rounded-2xl border border-[#E5E5EA] bg-[#FAFAFB] p-4 sm:p-5">
                    <p className="text-sm font-medium text-[#1D1D1F]">Kontakt</p>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div
                        ref={(node) => { fieldRefs.current["contact:first_name"] = node; }}
                        className="space-y-1"
                        data-testid="public-form-contact-first-name"
                        data-invalid={validationErrors["contact:first_name"] ? "true" : "false"}
                      >
                        <Label className={validationErrors["contact:first_name"] ? "text-[#D70015]" : ""}>Vorname{form?.require_contact_details ? " *" : ""}</Label>
                        <Input
                          className={`input-base ${validationErrors["contact:first_name"] ? "border-[#FF3B30] bg-red-50/40 focus-visible:ring-[#FF3B30]" : ""}`}
                          value={contact.first_name}
                          aria-invalid={Boolean(validationErrors["contact:first_name"])}
                          onChange={(e) => {
                            setContact((p) => ({ ...p, first_name: e.target.value }));
                            if (e.target.value.trim()) clearValidationError("contact:first_name");
                          }}
                        />
                        {validationErrors["contact:first_name"] && <p className="text-xs text-[#D70015]" role="alert">{validationErrors["contact:first_name"]}</p>}
                      </div>
                      <div
                        ref={(node) => { fieldRefs.current["contact:last_name"] = node; }}
                        className="space-y-1"
                        data-testid="public-form-contact-last-name"
                        data-invalid={validationErrors["contact:last_name"] ? "true" : "false"}
                      >
                        <Label className={validationErrors["contact:last_name"] ? "text-[#D70015]" : ""}>Nachname{form?.require_contact_details ? " *" : ""}</Label>
                        <Input
                          className={`input-base ${validationErrors["contact:last_name"] ? "border-[#FF3B30] bg-red-50/40 focus-visible:ring-[#FF3B30]" : ""}`}
                          value={contact.last_name}
                          aria-invalid={Boolean(validationErrors["contact:last_name"])}
                          onChange={(e) => {
                            setContact((p) => ({ ...p, last_name: e.target.value }));
                            if (e.target.value.trim()) clearValidationError("contact:last_name");
                          }}
                        />
                        {validationErrors["contact:last_name"] && <p className="text-xs text-[#D70015]" role="alert">{validationErrors["contact:last_name"]}</p>}
                      </div>
                      <div
                        ref={(node) => { fieldRefs.current["contact:email"] = node; }}
                        className="space-y-1"
                        data-testid="public-form-contact-email"
                        data-invalid={validationErrors["contact:email"] ? "true" : "false"}
                      >
                        <Label className={validationErrors["contact:email"] ? "text-[#D70015]" : ""}>E-Mail{form?.require_contact_details ? " *" : ""}</Label>
                        <Input
                          className={`input-base ${validationErrors["contact:email"] ? "border-[#FF3B30] bg-red-50/40 focus-visible:ring-[#FF3B30]" : ""}`}
                          type="email"
                          value={contact.email}
                          aria-invalid={Boolean(validationErrors["contact:email"])}
                          onChange={(e) => {
                            setContact((p) => ({ ...p, email: e.target.value }));
                            if (e.target.value.trim()) clearValidationError("contact:email");
                          }}
                        />
                        {validationErrors["contact:email"] && <p className="text-xs text-[#D70015]" role="alert">{validationErrors["contact:email"]}</p>}
                      </div>
                      <div className="space-y-1">
                        <Label>Telefon</Label>
                        <Input
                          className="input-base"
                          value={contact.phone}
                          onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {buildFieldRows(form.fields || []).map((row, rowIdx) => (
                  <div key={`field-row-${rowIdx}`} className="grid grid-cols-1 gap-4 md:grid-cols-6">
                    {row.map((field, colIdx) => {
                      const contentBorderless = Boolean(field.content_borderless);
                      const hasLabel = field.label !== undefined && field.label !== null ? field.label !== "" : true;
                      const labelText = hasLabel ? (field.label || field.key) : "";
                      const validationKey = `field:${field.key}`;
                      const fieldError = validationErrors[validationKey];
                      return (
                      <div
                        key={field.key || `${rowIdx}-${colIdx}`}
                        ref={(node) => { fieldRefs.current[validationKey] = node; }}
                        className={`space-y-2 ${getFieldSpanClass(field)} ${fieldError ? "rounded-xl bg-red-50/40 p-2 ring-1 ring-red-200" : ""}`}
                        data-testid={`public-form-field-${field.key}`}
                        data-invalid={fieldError ? "true" : "false"}
                      >
                        {field.type === "content" ? (
                          <div className={contentBorderless ? "py-1" : "rounded-xl border border-[#E5E5EA] bg-[#FAFAFB] px-4 py-3"}>
                            {hasLabel ? <p className="text-sm font-medium text-[#1D1D1F]">{labelText}</p> : null}
                            {isEmbeddedFormImage(field.content) ? (
                              <img src={field.content} alt={labelText || "Abbildung zum Formular"} className="mt-2 max-h-[480px] max-w-full object-contain" />
                            ) : (
                              <FormContentText content={String(field.content || "").trim()} className={`text-sm text-[#4B5563] ${hasLabel ? "mt-1" : ""}`} />
                            )}
                          </div>
                        ) : (
                          <>
                            {field.type !== "checkbox" && field.type !== "privacy" && hasLabel && (
                              <Label htmlFor={`public-field-${field.key}`} className={fieldError ? "text-[#D70015]" : ""}>
                                {labelText}
                                {field.required ? " *" : ""}
                              </Label>
                            )}
                            {field.type === "textarea" ? (
                              <Textarea
                                id={`public-field-${field.key}`}
                                className={`input-base ${fieldError ? "border-[#FF3B30] bg-red-50/40 focus-visible:ring-[#FF3B30]" : ""}`}
                                placeholder={String(field.placeholder || "").trim() || undefined}
                                value={values[field.key] || ""}
                                aria-invalid={Boolean(fieldError)}
                                onChange={(e) => {
                                  setValues((p) => ({ ...p, [field.key]: e.target.value }));
                                  if (e.target.value.trim()) clearValidationError(validationKey);
                                }}
                              />
                            ) : field.type === "checkbox" ? (
                              <label className={`flex cursor-pointer items-start gap-3 rounded-xl border border-[#E5E5EA] p-4 text-sm leading-6 ${fieldError ? "text-[#D70015]" : "text-[#1D1D1F]"}`}>
                                <Checkbox
                                  checked={Boolean(values[field.key])}
                                  className={fieldError ? "border-[#FF3B30] ring-1 ring-[#FF3B30]" : ""}
                                  aria-invalid={Boolean(fieldError)}
                                  onCheckedChange={(checked) => {
                                    setValues((p) => ({ ...p, [field.key]: Boolean(checked) }));
                                    if (checked) clearValidationError(validationKey);
                                  }}
                                />
                                {labelText}
                                {hasLabel && field.required ? " *" : ""}
                              </label>
                            ) : field.type === "privacy" ? (
                              <div className="flex items-start gap-3 text-sm text-[#1D1D1F]">
                                <Checkbox
                                  checked={Boolean(values[field.key])}
                                  className={fieldError ? "border-[#FF3B30] ring-1 ring-[#FF3B30]" : ""}
                                  aria-invalid={Boolean(fieldError)}
                                  onCheckedChange={(checked) => {
                                    setValues((p) => ({ ...p, [field.key]: Boolean(checked) }));
                                    if (checked) clearValidationError(validationKey);
                                  }}
                                />
                                <span className="leading-6">
                                  Ich stimme der{" "}
                                  {resolvePrivacySource(field) === "custom" ? (
                                    <a
                                      className="text-[#0071E3] hover:underline"
                                      href={String(field?.privacy_policy_url || "").trim() || "#"}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      Datenschutzerklärung
                                    </a>
                                  ) : (
                                    <button
                                      type="button"
                                      className="text-[#0071E3] hover:underline"
                                      onClick={() => setPrivacyModalOpen(true)}
                                    >
                                      Datenschutzerklärung
                                    </button>
                                  )}{" "}
                                  zu.
                                </span>
                              </div>
                            ) : field.type === "select" ? (
                              <AppSelect
                                id={`public-field-${field.key}`}
                                className={`w-full input-base ${fieldError ? "border-[#FF3B30] bg-red-50/40 focus:ring-[#FF3B30]" : ""}`}
                                value={values[field.key] || ""}
                                aria-invalid={Boolean(fieldError)}
                                onChange={(e) => {
                                  setValues((p) => ({ ...p, [field.key]: e.target.value }));
                                  if (e.target.value.trim()) clearValidationError(validationKey);
                                }}
                              >
                                <option value="">Auswählen</option>
                                {(Array.isArray(field.options) ? field.options : String(field.options || "").split(",").map((o) => o.trim()).filter(Boolean))
                                  .map((option) => (
                                    <option key={`${field.key}-${option}`} value={option}>
                                      {option}
                                    </option>
                                  ))}
                              </AppSelect>
                            ) : field.type === "signature" ? (
                              <SignaturePad
                                value={values[field.key] || ""}
                                className={fieldError ? "text-[#D70015]" : ""}
                                onChange={(v) => {
                                  setValues((p) => ({ ...p, [field.key]: v }));
                                  if (v) clearValidationError(validationKey);
                                }}
                                disabled={submitting}
                              />
                            ) : (
                              <Input
                                className={`input-base ${fieldError ? "border-[#FF3B30] bg-red-50/40 focus-visible:ring-[#FF3B30]" : ""}`}
                                id={`public-field-${field.key}`}
                                type={field.type === "date" ? "date" : "text"}
                                placeholder={field.type === "date" ? "TT.MM.JJJJ" : (String(field.placeholder || "").trim() || undefined)}
                                value={values[field.key] || ""}
                                aria-invalid={Boolean(fieldError)}
                                onChange={(e) => {
                                  setValues((p) => ({ ...p, [field.key]: e.target.value }));
                                  if (e.target.value.trim()) clearValidationError(validationKey);
                                }}

                              />
                            )}
                            {fieldError && <p className="text-xs font-medium text-[#D70015]" role="alert">{fieldError}</p>}
                          </>
                        )}
                      </div>
                      );
                    })}
                  </div>
                ))}
                <div className="flex flex-col gap-4 border-t border-[#E5E5EA] pt-6 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-5 text-[#86868B]" role="status" aria-live="polite">{submitting ? "Dein Formular wird übermittelt. Bitte lasse diese Seite geöffnet." : "Prüfe deine Angaben vor dem Absenden."}</p>
                  <Button type="button" className="btn-primary h-11 w-full gap-2 rounded-xl px-6 sm:w-auto" onClick={submit} disabled={submitting}>
                    <Send className="h-4 w-4" aria-hidden="true" />
                    {submitting ? "Wird gesendet …" : "Formular absenden"}
                  </Button>
                </div>
              </fieldset>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
